import { useState, useEffect, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiRequest, openApiFile } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { PageHeader } from '../../../../shared/components/PageHeader.js';
import { Card } from '../../../../shared/components/Card.js';
import { Button } from '../../../../shared/components/Button.js';
import { Badge, type BadgeTone } from '../../../../shared/components/Badge.js';
import { Alert } from '../../../../shared/components/Alert.js';
import { EmptyState } from '../../../../shared/components/EmptyState.js';
import { EligibilityPicker } from './EligibilityPicker.js';
import { DetailList } from './DetailList.js';
import { usePaymentTaxText } from './usePaymentTaxText.js';
import type { PaymentTaxes } from '../../../../shared/contracts/payment-taxes.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

/** A payment advice (t183), as GET /v1/payments/advice/eligibility/:id lists it. */
interface Advice {
  adviceId: string;
  adviceNumber: string;
  adviceDate?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  grossAmount?: number;
  tdsAmount?: number;
  otherDeductions?: number;
  netAmount?: number;
  paymentModeCode?: string;
  utrNumber?: string | null;
  paymentDate?: string;
  adviceStatusCode?: string;
  emailAddress?: string | null;
}

const STATUS_TONE: Record<string, BadgeTone> = {
  GENERATED: 'info',
  SENT: 'success',
  ACKNOWLEDGED: 'success',
  FAILED: 'danger',
};

/** L15.13 — Payment Advice: the advice issued for a bill's payment, to read, download or send. */
export function PaymentAdvicePage(): ReactNode {
  const { day } = useBusinessDay();
  const { t } = useTranslation();
  // Amounts follow the tenant's DISPLAY_NUMBER_FORMAT, not a locale in the source.
  const { money } = useBusinessNumber();
  const { token } = useAuth();
  const [params, setParams] = useSearchParams();
  const [eligId, setEligId] = useState(params.get('eligibilityId') ?? '');
  const [stage, setStage] = useState('');
  const [data, setData] = useState<Advice | null>(null);
  const [taxes, setTaxes] = useState<PaymentTaxes | null>(null);
  const { gstItems, gstNote, tdsBasis } = usePaymentTaxText();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<'send' | 'pdf' | null>(null);

  const load = (id: string) => {
    if (!id.trim()) return;
    setLoading(true);
    setError(null);
    setNotice(null);
    setData(null);
    setTaxes(null);
    // Every advice raised for the eligibility is kept; the newest comes first.
    apiRequest<Advice[]>(`/v1/payments/advice/eligibility/${id.trim()}`, { token })
      .then(async (rows) => {
        const advice = rows[0] ?? null;
        setData(advice);
        if (advice) setTaxes(await apiRequest<PaymentTaxes>(`/v1/payments/advice/${advice.adviceId}/taxes`, { token }));
      })
      .catch(e => setError((e as Error).message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const id = params.get('eligibilityId');
    if (id) load(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSend = async () => {
    if (!data?.adviceId) return;
    setBusy('send');
    setError(null);
    try {
      await apiRequest<unknown>(`/v1/payments/advice/${data.adviceId}/send`, { token, method: 'PATCH', body: { sendVia: 'EMAIL' } });
      setNotice(t('finance.advice.sent'));
      load(eligId);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  };

  const handleDownload = async () => {
    if (!data?.adviceId) return;
    setBusy('pdf');
    setError(null);
    try {
      await openApiFile(`/v1/payments/advice/${data.adviceId}/download`, token);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  };

  const status = data?.adviceStatusCode;

  return (
    <div className="tl-page" style={{ padding: 24, maxWidth: 960 }}>
      <PageHeader title={t('finance.advice.title')} subtitle={t('finance.advice.subtitle')} />
      <EligibilityPicker
        value={eligId}
        onPick={(id, s) => {
          setEligId(id);
          setStage(s);
          setParams(id ? { eligibilityId: id } : {});
          if (id) load(id); else { setData(null); setError(null); setNotice(null); }
        }}
      />
      {error && <Alert tone="danger">{error}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}
      {loading && <p style={{ color: 'var(--color-text-muted)' }}>{t('finance.advice.loading')}</p>}
      {!loading && !data && !error && !eligId && <EmptyState title={t('finance.advice.empty')} />}
      {!loading && !data && !error && eligId && (
        <Alert tone="info">
          <span className="tl-stack tl-stack--row" style={{ gap: 10, alignItems: 'center' }}>
            {t('finance.advice.notPaid')}
            {stage && <Badge tone="neutral">{t(`finance.stage.${stage}`)}</Badge>}
          </span>
        </Alert>
      )}
      {data && (
        <Card
          data-testid="advice-card"
          title={(
            <div className="tl-stack tl-stack--row" style={{ gap: 12, alignItems: 'center' }}>
              <h2 className="tl-card__title" style={{ margin: 0 }}>{data.adviceNumber}</h2>
              {status && <Badge tone={STATUS_TONE[status] ?? 'neutral'} dot>{t(`finance.adviceStatus.${status}`)}</Badge>}
            </div>
          )}
          actions={(
            <div className="tl-stack tl-stack--row" style={{ gap: 8 }}>
              <Button variant="secondary" onClick={() => void handleSend()} loading={busy === 'send'} disabled={busy !== null}>
                {t('finance.advice.sendEmail')}
              </Button>
              <Button variant="primary" onClick={() => void handleDownload()} loading={busy === 'pdf'} disabled={busy !== null}>
                {busy === 'pdf' ? t('finance.advice.downloading') : t('finance.advice.downloadPdf')}
              </Button>
            </div>
          )}
        >
          <DetailList
            items={[
              { label: t('finance.advice.rowInvoice'), value: data.invoiceNumber ?? '—' },
              { label: t('finance.advice.rowInvoiceDate'), value: day(data.invoiceDate) },
              ...(taxes ? gstItems(taxes.gst) : []),
              { label: t('finance.advice.rowGross'), value: money(data.grossAmount ?? 0), testId: 'advice-gross' },
              { label: t('finance.advice.rowTds'), value: money(data.tdsAmount ?? 0), testId: 'advice-tds' },
              ...(taxes?.tds ? [{ label: t('finance.tax.tdsBasis'), value: tdsBasis(taxes.tds), testId: 'advice-tds-basis' }] : []),
              { label: t('finance.advice.rowOther'), value: money(data.otherDeductions ?? 0) },
              { label: t('finance.advice.rowNet'), value: money(data.netAmount ?? 0), strong: true, testId: 'advice-net' },
              { label: t('finance.advice.rowMode'), value: data.paymentModeCode ?? '—' },
              { label: t('finance.advice.rowTxnRef'), value: data.utrNumber ?? '—', testId: 'advice-utr' },
              { label: t('finance.advice.rowPaymentDate'), value: day(data.paymentDate) },
              { label: t('finance.advice.rowEmail'), value: data.emailAddress ?? '—' },
            ]}
          />
          {taxes && (
            <p data-testid="advice-gst-note" style={{ margin: '16px 0 0', color: 'var(--color-text-muted)' }}>
              {gstNote(taxes.gst)}
            </p>
          )}
        </Card>
      )}
    </div>
  );
}
